import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const PersonalityWorlds = () => {
  const navigate = useNavigate();

  const worlds = [
    {
      id: 'community',
      title: 'Community Guy',
      subtitle: 'Building Connections',
      description: 'Leading developer communities, organizing events, and fostering collaboration across tech ecosystems.',
      icon: 'Users',
      color: 'success',
      bgGradient: 'from-success/10 to-success/5',
      path: '/community-guy-world',
      stats: [
        { label: 'Communities Led', value: '3+' },
        { label: 'Events Organized', value: '15+' },
        { label: 'Developers Mentored', value: '100+' }
      ],
      highlights: ['GDSC Lead', 'AngelHack Community Builder', 'StackUp Council Member']
    },
    {
      id: 'web3',
      title: 'Web3 Developer',
      subtitle: 'Decentralized Innovation',
      description: 'Creating blockchain solutions, smart contracts, and DeFi applications that shape the future of finance.',
      icon: 'Zap',
      color: 'warning',
      bgGradient: 'from-warning/10 to-warning/5',
      path: '/web3-developer-world',
      stats: [
        { label: 'DApps Built', value: '5+' },
        { label: 'Smart Contracts', value: '10+' },
        { label: 'Blockchains', value: '3+' }
      ],
      highlights: ['Solidity Expert', 'DeFi Protocols', 'NFTs']
    },
    {
      id: 'software',
      title: 'Software Developer',
      subtitle: 'Crafting Solutions',
      description: 'Building scalable web applications, mobile apps, and enterprise solutions with modern technologies.',
      icon: 'Code',
      color: 'primary',
      bgGradient: 'from-primary/10 to-primary/5',
      path: '/software-developer-world',
      stats: [
        { label: 'Projects Delivered', value: '10+' },
        { label: 'Years Experience', value: '3+' },
        { label: 'Technologies', value: '10+' }
      ],
      highlights: ['Full-Stack Development', 'AI', 'Mobile Development']
    }
  ];

  const handleWorldClick = (path) => {
    navigate(path);
  };

  return (
    <section className="py-20 bg-surface" id="personality-worlds">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Explore My{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Three Worlds
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Each world represents a unique facet of my professional journey. Click to dive deep into my experiences, projects, and achievements in each domain.
          </p>
        </motion.div>

        {/* Worlds Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {worlds.map((world, index) => (
            <motion.div
              key={world.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group cursor-pointer"
              onClick={() => handleWorldClick(world.path)}
            >
              <div className={`relative bg-gradient-to-br ${world.bgGradient} border border-${world.color}/20 rounded-2xl p-8 h-full shadow-elevation-2 group-hover:shadow-elevation-3 transition-all duration-300`}>
                {/* Icon */}
                <div className={`w-16 h-16 bg-${world.color}/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={world.icon} size={32} className={`text-${world.color}`} />
                </div>

                {/* Content */}
                <div className="mb-6">
                  <h3 className="font-heading text-2xl font-bold text-primary mb-2">
                    {world.title}
                  </h3>
                  <p className={`font-body text-${world.color} font-medium mb-4`}>
                    {world.subtitle}
                  </p>
                  <p className="font-body text-text-secondary leading-relaxed">
                    {world.description}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {world.stats.map((stat, statIndex) => (
                    <div key={statIndex} className="text-center">
                      <div className={`font-heading text-xl font-bold text-${world.color}`}>
                        {stat.value}
                      </div>
                      <div className="font-caption text-text-secondary text-xs">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Highlights */}
                <div className="space-y-2 mb-6">
                  {world.highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex} className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-${world.color} rounded-full`}></div>
                      <span className="font-caption text-text-secondary text-sm">
                        {highlight}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Enter Button */}
                <div className="flex items-center justify-between">
                  <span className={`font-body font-medium text-${world.color} group-hover:text-primary transition-colors duration-300`}>
                    Enter World
                  </span>
                  <Icon 
                    name="ArrowRight" 
                    size={20} 
                    className={`text-${world.color} group-hover:text-primary group-hover:translate-x-1 transition-all duration-300`} 
                  />
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-primary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="font-body text-text-secondary mb-6">
            Can't decide where to start? Let me guide you through my journey.
          </p>
          <button className="bg-accent text-surface px-8 py-4 rounded-lg font-body font-medium hover:bg-accent/90 transition-all duration-200 transform hover:scale-105 shadow-elevation-2">
            Take the Guided Tour
          </button>
        </motion.div> */}
      </div>
    </section>
  );
};

export default PersonalityWorlds;